import { useMemo } from "react";
import { motion } from "framer-motion";

const C = 300;
const N = 170;
const LIME = "#C6EE5A";

function rng(seed) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function layoutNodes(geo) {
  const list = geo.slice(0, 8);
  const n = list.length;
  return list.map((g, i) => {
    const a = n <= 3 ? (-150 + (n === 1 ? 60 : (120 / (n - 1)) * i)) * (Math.PI / 180) : -Math.PI / 2 + (i / n) * Math.PI * 2;
    return { ...g, x: C + Math.cos(a) * 215, y: C + Math.sin(a) * 215 };
  });
}

function particleState(p, stage, nodes) {
  let x = p.hx;
  let y = p.hy;
  let op = 0;
  let fill = "rgba(255,255,255,0.9)";
  if (stage === 0) {
    x = nodes[p.ni].x;
    y = nodes[p.ni].y;
  } else if (stage === 1) op = 0.55;
  else if (stage === 2) {
    op = p.match ? 1 : 0.16;
    if (p.match) fill = LIME;
  } else if (p.match) {
    const k = stage === 3 ? 1 : stage === 4 ? 0.55 : stage === 5 ? 0.3 : 0.12;
    x = C + (p.cx - C) * k;
    y = C + (p.cy - C) * k;
    op = stage >= 6 ? 0 : 1;
    fill = LIME;
  } else op = stage === 3 ? 0.06 : 0;
  return {
    transform: `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px)`,
    opacity: op,
    fill,
    transition: `transform 950ms cubic-bezier(.22,1,.36,1) ${p.delay}ms, opacity 600ms ease ${p.delay * 0.5}ms, fill 500ms ease ${p.delay * 0.4}ms`,
  };
}

export function CompilerVisual({ stage, audience }) {
  const nodes = useMemo(() => layoutNodes(audience.geography), [audience.geography]);
  const particles = useMemo(() => {
    const r = rng(7);
    const silverSum = nodes.reduce((a, n) => a + n.silverTotal, 0) || 1;
    const out = [];
    nodes.forEach((node, ni) => {
      const count = Math.max(10, Math.round((N * node.silverTotal) / silverSum));
      const matchCount = Math.round(count * (node.silverTotal ? node.households / node.silverTotal : 0));
      for (let k = 0; k < count; k += 1) {
        const a = r() * Math.PI * 2;
        const rad = 16 + Math.sqrt(r()) * 58;
        const ca = r() * Math.PI * 2;
        const cr = Math.sqrt(r()) * 92;
        out.push({ hx: node.x + Math.cos(a) * rad, hy: node.y + Math.sin(a) * rad, cx: C + Math.cos(ca) * cr, cy: C + Math.sin(ca) * cr, match: k < matchCount, ni, delay: Math.round(r() * 360) });
      }
    });
    return out;
  }, [nodes]);
  const done = stage >= 6;

  return (
    <svg viewBox="0 0 600 600" className="h-full w-full overflow-visible" aria-hidden="true">
      <defs>
        <radialGradient id="cv-halo">
          <stop offset="0" stopColor="rgba(198,238,90,0.45)" />
          <stop offset="1" stopColor="rgba(150,193,31,0)" />
        </radialGradient>
      </defs>
      <circle cx={C} cy={C} r="92" fill="none" stroke="rgba(255,255,255,0.06)" />
      <circle cx={C} cy={C} r="160" fill="none" stroke="rgba(255,255,255,0.05)" />
      <g className="animate-spin-slow" style={{ transformOrigin: "300px 300px" }}>
        <circle cx={C} cy={C} r="262" fill="none" stroke="rgba(198,238,90,0.25)" strokeDasharray="2 10" />
        <circle cx={C} cy={C - 262} r="3" fill={LIME} />
      </g>

      {nodes.map((n, i) => (
        <motion.line key={`l-${n.code}`} x1={C} y1={C} x2={n.x} y2={n.y} stroke="rgba(198,238,90,0.28)" strokeWidth="1"
          initial={{ pathLength: 0, opacity: 1 }} animate={{ pathLength: 1, opacity: stage >= 3 ? 0.35 : 1 }} transition={{ duration: 0.9, delay: 0.15 + i * 0.08 }} />
      ))}

      <circle cx={C} cy={C} r="150" fill="url(#cv-halo)" style={{ opacity: stage >= 3 ? 1 : 0, transition: "opacity 900ms ease" }} />

      {particles.map((p, i) => (
        <circle key={i} cx="0" cy="0" r="2.3" style={particleState(p, stage, nodes)} />
      ))}

      {nodes.map((n, i) => (
        <motion.g key={n.code} initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.1 + i * 0.08, ease: [0.22, 1, 0.36, 1] }} style={{ transformOrigin: `${n.x}px ${n.y}px` }}>
          <circle cx={n.x} cy={n.y} r="11" fill="#0B1C0D" stroke={LIME} strokeWidth="1.4" />
          <circle cx={n.x} cy={n.y} r="4" fill={LIME} />
          <text x={n.x} y={n.y - 22} textAnchor="middle" fontSize="13" fontWeight="700" letterSpacing="1.2" fill="#fff" className="font-ui">{n.code}</text>
          <text x={n.x} y={n.y + 30} textAnchor="middle" fontSize="10.5" letterSpacing="0.8" fill="rgba(255,255,255,0.5)" className="font-ui">{n.silverTotal.toLocaleString("en-GB")}</text>
        </motion.g>
      ))}
      {audience.geography.length > 8 && (
        <text x={C} y={590} textAnchor="middle" fontSize="11" fill="rgba(255,255,255,0.5)" className="font-ui">+ {audience.geography.length - 8} more districts</text>
      )}

      <g style={{ opacity: stage >= 4 ? 1 : 0, transition: "opacity 700ms ease" }}>
        <motion.rect x={C - 64} y={C - 42} width="128" height="84" rx="8" fill={stage >= 5 ? "rgba(255,255,255,0.96)" : "rgba(255,255,255,0)"} stroke={LIME} strokeWidth="1.6"
          initial={{ pathLength: 0 }} animate={{ pathLength: stage >= 4 ? 1 : 0 }} transition={{ duration: 0.8 }} style={{ transition: "fill 700ms ease" }} />
        <motion.path d={`M${C - 64} ${C - 36} L${C} ${C + 8} L${C + 64} ${C - 36}`} fill="none" stroke={stage >= 5 ? "rgba(18,41,15,0.25)" : LIME} strokeWidth="1.6"
          initial={{ pathLength: 0 }} animate={{ pathLength: stage >= 4 ? 1 : 0 }} transition={{ duration: 0.8, delay: 0.2 }} />
        <circle cx={C} cy={C + 8} r="8" fill="#96C11F" style={{ opacity: stage >= 5 ? 1 : 0, transition: "opacity 500ms ease 300ms" }} />
      </g>

      {done && (
        <g>
          {[0, 1].map((i) => (
            <circle key={i} cx={C} cy={C} r="80" fill="none" stroke={LIME} strokeWidth="1.5" className="animate-pulse-ring" style={{ transformOrigin: "300px 300px", animationDelay: `${i * 0.6}s` }} />
          ))}
          <motion.g initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 16 }} style={{ transformOrigin: `${C + 64}px ${C - 42}px` }}>
            <circle cx={C + 64} cy={C - 42} r="22" fill="#96C11F" />
            <motion.path d={`M${C + 54} ${C - 42} l7 7 l13 -14`} fill="none" stroke="#12290F" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.45, delay: 0.2 }} />
          </motion.g>
        </g>
      )}
    </svg>
  );
}
